import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const QUICK_ACTIONS = [
  {
    id: 'buy',
    label: 'Buy Stock',
    icon: 'TrendingUp',
    color: 'var(--color-success)',
    path: '/stock-exchange',
    description: 'Open buy order',
  },
  {
    id: 'sell',
    label: 'Sell Stock',
    icon: 'TrendingDown',
    color: 'var(--color-error)',
    path: '/portfolio',
    description: 'Open sell order',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: 'PieChart',
    color: 'var(--color-primary)',
    path: '/portfolio',
    description: 'View holdings',
  },
  {
    id: 'market',
    label: 'Market',
    icon: 'BarChart2',
    color: 'var(--color-accent)',
    path: '/stock-exchange',
    description: 'Live market data',
  },
];

const QuickActionPanel = ({ isCollapsed = false, className = '' }) => {
  const navigate = useNavigate();
  const [confirmAction, setConfirmAction] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleActionClick = (action) => {
    if (action?.id === 'buy' || action?.id === 'sell') {
      setConfirmAction(action);
    } else {
      navigate(action?.path);
    }
  };

  const handleConfirm = () => {
    if (confirmAction) {
      navigate(confirmAction?.path);
      setConfirmAction(null);
    }
  };

  if (isCollapsed) {
    return (
      <div className={`flex flex-col items-center gap-2 px-3 py-3 border-t border-border ${className}`}>
        {QUICK_ACTIONS?.slice(0, 2)?.map((action) => (
          <button
            key={action?.id}
            onClick={() => handleActionClick(action)}
            className="quick-action-btn"
            title={action?.label}
          >
            <Icon name={action?.icon} size={16} color={action?.color} />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`px-4 py-3 border-t border-border ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-2 hover:opacity-80 transition-opacity duration-250"
      >
        <div className="flex items-center gap-2">
          <Icon name="Zap" size={13} color="var(--color-accent)" />
          <span className="font-caption text-xs text-muted-foreground uppercase tracking-widest">Quick Actions</span>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={13}
          color="var(--color-muted-foreground)"
        />
      </button>
      {/* Actions Grid */}
      {isExpanded && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {QUICK_ACTIONS?.map((action) => (
            <button
              key={action?.id}
              onClick={() => handleActionClick(action)}
              className="flex flex-col items-center gap-1.5 p-2.5 rounded-md border border-border bg-secondary/50 hover:bg-primary/8 hover:border-primary/30 transition-all duration-250 cursor-pointer group"
              title={action?.description}
            >
              <div
                className="w-8 h-8 rounded flex items-center justify-center"
                style={{ background: `${action?.color}15` }}
              >
                <Icon name={action?.icon} size={15} color={action?.color} />
              </div>
              <span className="font-caption text-xs text-muted-foreground group-hover:text-foreground transition-colors duration-250 text-center leading-tight">
                {action?.label}
              </span>
            </button>
          ))}
        </div>
      )}
      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 z-modal flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div
            className="bg-card border border-border rounded-lg p-6 w-80 shadow-glow-md"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-md flex items-center justify-center"
                style={{ background: `${confirmAction?.color}15` }}
              >
                <Icon name={confirmAction?.icon} size={20} color={confirmAction?.color} />
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground">{confirmAction?.label}</h4>
                <p className="font-caption text-xs text-muted-foreground">{confirmAction?.description}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Navigate to {confirmAction?.label?.toLowerCase()} screen?
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                fullWidth
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionPanel;